import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { getTodoScenarioActions } from '../actions/getTodoScenarioActions';
import { getScenarioActions } from '../actions/getScenarioActions';
import { QuickAdd } from 'owa-todo-views';
import { getQuickAddViewState } from '../selectors/agendaStoreTodoViewSelectors';
import type { OwaDate } from 'owa-datetime';
import loc from 'owa-localize';
import { createNewAgendaTaskText } from './AgendaTodoQuickAdd.locstring.json';
import { getTodoCache } from '../store/todoCache';
import { getPropertiesForInsert } from 'owa-todo-utils';
import { TodoDescriptorType } from 'owa-todo-cache';

export interface AgendaTodoQuickAddProps {
    scenarioId: string;
    date: OwaDate;
}

import styles from './AgendaTodoQuickAdd.scss';

export const AgendaTodoQuickAdd = observer(function AgendaTodoQuickAdd(
    props: AgendaTodoQuickAddProps
) {
    const { scenarioId, date } = props;
    const { createTodos } = getTodoScenarioActions(scenarioId, 'QuickAdd');
    const { updateQuickAddViewState } = getScenarioActions(scenarioId);
    const uiState = getQuickAddViewState(scenarioId);
    const { subject } = uiState;

    const onAddClicked = React.useCallback(() => {
        if (subject && subject.trim().length > 0) {
            const todoItems = getTodoCache(scenarioId).getItemsForLock({
                type: TodoDescriptorType.DueDate,
                startDate: date,
                endDate: date,
            });
            const properties = todoItems
                ? getPropertiesForInsert(0, todoItems, 'OrderDateTime')
                : {};
            createTodos([{ Subject: subject, DueDateTime: date, ...properties }], null);
            updateQuickAddViewState({ subject: '', linkedEmailEntity: null });
        }
    }, [subject]);

    const updateQuickAddSubject = React.useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string) => {
            updateQuickAddViewState({ subject: newValue });
        },
        []
    );

    const onQuickAddFocus = React.useCallback(() => {
        updateQuickAddViewState({ isFocused: true });
    }, []);

    const onQuickAddBlur = React.useCallback(() => {
        updateQuickAddViewState({ isFocused: false });

        // If user has typed empty spaces, placeholder won't be shown.
        // This makes the UI look really awkward. So clean out any empty spaces.
        if (subject.length > 0 && subject.trim().length === 0) {
            updateQuickAddViewState({ subject: '' });
        }
    }, [subject]);

    return (
        <QuickAdd
            uiState={uiState}
            onAddClicked={onAddClicked}
            onSubjectChange={updateQuickAddSubject}
            onFocus={onQuickAddFocus}
            onBlur={onQuickAddBlur}
            quickAddTitle={loc(createNewAgendaTaskText)}
            className={styles.quickAdd}
        />
    );
});
